/**
 * Login page — email/password authentication.
 * After successful password auth, MFA routing is handled by ProtectedRoute.
 */
import { useState } from 'react'
import { useAuth } from './AuthContext'
import { useNavigate, Link } from 'react-router-dom'

const inputClass =
  'w-full px-4 py-3 bg-surface-tertiary border border-edge rounded-lg text-ink placeholder:text-ink-subtle focus:outline-none focus:ring-2 focus:ring-brand focus:border-transparent'

export default function LoginPage() {
  const { signIn } = useAuth()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState(null)
  const [loading, setLoading] = useState(false)

  async function handleSubmit(e) {
    e.preventDefault()
    setError(null)
    setLoading(true)

    try {
      await signIn(email, password)
      // ProtectedRoute will handle MFA redirect if needed
      navigate('/dashboard')
    } catch (err) {
      setError(err.message || 'Login failed')
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen bg-surface flex items-center justify-center px-4 py-6 sm:py-10">
      <div className="w-full max-w-md">
        <div className="text-center mb-6 sm:mb-8">
          <h1 className="text-2xl sm:text-3xl font-bold text-ink">PE Portfolio Dashboard</h1>
          <p className="text-ink-muted mt-2">Sign in to your account</p>
        </div>

        <form onSubmit={handleSubmit} className="bg-surface-secondary rounded-xl p-5 sm:p-8 border border-edge-subtle">
          {error && (
            <div className="bg-danger/40 border border-danger-border rounded-lg p-3 mb-6 text-danger-fg text-sm">
              {error}
            </div>
          )}

          <div className="mb-5">
            <label className="block text-sm font-medium text-ink-muted mb-2">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              className={inputClass}
              placeholder="you@example.com"
              required
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-ink-muted mb-2">Password</label>
            <input
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={inputClass}
              placeholder="Enter your password"
              required
            />
          </div>

          <button
            type="submit"
            disabled={loading}
            className="w-full py-3 bg-brand hover:opacity-90 disabled:opacity-50 text-ink-onAccent font-semibold rounded-lg transition"
          >
            {loading ? 'Signing in...' : 'Sign In'}
          </button>

          <p className="text-center text-ink-muted text-sm mt-6">
            Don&apos;t have an account?{' '}
            <Link to="/signup" className="text-brand hover:text-brand-secondary">
              Create one
            </Link>
          </p>
        </form>
      </div>
    </div>
  )
}
